import {useFocusTrap} from '@primer/react'
import {ThemeProvider} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useRef} from 'react'

import {MarketingNavigation} from '@github-ui/marketing-navigation/MarketingNavigation'

import {DesktopAuthActions} from './components/AuthCTAs/DesktopAuthActions'
import {MobileAuthActions} from './components/AuthCTAs/MobileAuthActions'
import {HeaderLogo} from './components/HeaderLogo/HeaderLogo'
import {HeaderMenuToggle} from './components/HeaderMenuToggle/HeaderMenuToggle'
import {MobileHeaderTitle} from './components/MobileHeaderTitle/MobileHeaderTitle'
import {HeaderSearch} from './components/Search/HeaderSearch'
import {useHeaderIsTop} from './hooks/use-header-is-top'
import {useHeaderMenu} from './hooks/use-header-menu'
import {useNavPaddingRecalculation} from './hooks/use-nav-padding-recalculation'
import type {MarketingHeaderProps} from './marketing-header.types'
import styles from './MarketingHeader.module.css'

/**
 * Composer for the marketing (logged-out) header, rendered from Rails via
 * `render_react_partial name: "marketing-header"` behind the `site_marketing_header_react` flag.
 *
 * Intentionally omits the legacy `js-*` behavior hooks: they're still wired to the shipping global JS and
 * would double-bind and fight React. Open state and the `open` / `Details--on` classes come from React.
 */
export function MarketingHeader(props: MarketingHeaderProps) {
  const {
    color_mode = 'dark',
    variant,
    overlay = false,
    fixed = false,
    mobile_header_title,
    should_use_dotcom_links = false,
    show_search_and_nav = true,
    hide_search = false,
    private_mode_enabled = false,
    logged_in = false,
    marketing_page = false,
    signup_enabled = false,
    is_signup_controller = false,
    login_path = '/login',
    signup_path = '/signup',
    home_path = '/',
    auth_hydro_click,
    auth_hydro_click_hmac,
  } = props

  const hydro = {auth_hydro_click, auth_hydro_click_hmac}

  // Mirrors the legacy Rails gate — shown unless `hide_search` or private mode, independent of
  // `show_search_and_nav` (nav-only).
  const searchVisible = !hide_search && !private_mode_enabled

  const {isOpen, close, toggle, toggleRef} = useHeaderMenu()
  const headerRef = useRef<HTMLElement | null>(null)
  const headerMenuRef = useRef<HTMLDivElement | null>(null)

  useNavPaddingRecalculation({headerRef, headerMenuRef, toggleRef, isOpen})
  const {isTop} = useHeaderIsTop(headerRef, fixed)

  // Contain Tab focus within the header while the mobile overlay is open so keyboard
  // users can't reach the page behind it (legacy used a trailing sr-only trap button).
  useFocusTrap({containerRef: headerRef, initialFocusRef: toggleRef, disabled: !isOpen})

  // Older Rails payloads always include a variant and use both `default` and `dark` for dark headers while
  // sending light mode. New payloads omit variant so the explicit page color mode remains authoritative.
  const resolvedColorMode = variant === 'default' || variant === 'dark' || color_mode !== 'light' ? 'dark' : 'light'

  return (
    <ThemeProvider colorMode={resolvedColorMode} data-light-theme="light" data-dark-theme="dark">
      <header
        ref={headerRef}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx(styles.root, 'HeaderMktg', 'header-logged-out', {
          [styles.overlay]: overlay,
          [styles.fixed]: fixed,
          [styles.open]: isOpen,
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          open: isOpen,
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          'Details--on': isOpen,
        })}
        role="banner"
        data-marketing-header="true"
        data-color-mode={resolvedColorMode}
        data-light-theme="light"
        data-dark-theme="dark"
        data-is-top={String(isTop)}
      >
        {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
        <h2 className={styles.visuallyHidden}>Navigation Menu</h2>

        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation menu"
          onClick={() => close({restoreFocus: true})}
        />

        <div className={styles.bar}>
          <div className={styles.topRow}>
            <div className={styles.toggleSlot}>
              <HeaderMenuToggle ref={toggleRef} isOpen={isOpen} onToggle={toggle} />
            </div>

            <HeaderLogo href={home_path} isMenuOpen={isOpen} />

            <MobileAuthActions
              loggedIn={logged_in}
              marketingPage={marketing_page}
              loginPath={login_path}
              hydro={hydro}
              isMenuOpen={isOpen}
            />
          </div>

          {mobile_header_title ? <MobileHeaderTitle title={mobile_header_title} isMenuOpen={isOpen} /> : null}

          <div ref={headerMenuRef} className={styles.menu}>
            <div className={styles.menuWrapper}>
              {show_search_and_nav && <MarketingNavigation should_use_dotcom_links={should_use_dotcom_links} />}

              <div className={styles.ctaContainer}>
                {searchVisible && <HeaderSearch onOpen={close} />}

                <DesktopAuthActions
                  loggedIn={logged_in}
                  marketingPage={marketing_page}
                  signupEnabled={signup_enabled}
                  isSignupController={is_signup_controller}
                  loginPath={login_path}
                  signupPath={signup_path}
                  hydro={hydro}
                />
              </div>
            </div>
          </div>
        </div>

        {!overlay && <div className={styles.bottomBorder} aria-hidden="true" />}
      </header>
    </ThemeProvider>
  )
}
